import React, {useCallback, useState} from 'react';
import './CaseFinancialSummary.less';
import Matter from '../../entities/matter';
import FinancialHelper from '../../Financial/FinancialHelper';
import util from '../../lib/util';
import IconButton from '../../ui/IconButton/IconButton';
import Icons from '../../ui/IconButton/Icons';
import i18n from '../../i18n/i18n';
import {Tooltip} from "@mui/material";
import {isPortalUser} from "../../lib/picos/currentLogin";

const CaseFinancialSummary = (props: CaseFinancialSummaryProps) => {
    const {matter, standalone, hideMessage} = props;

    const [showLifeTimeStats, setShowLifeTimeStats] = useState<boolean>(props.showLifeTimeStats);

    const onMore = useCallback(() => {
        setShowLifeTimeStats(true);
    }, [setShowLifeTimeStats]);

    const renderUnbilled = useCallback((unbilled: number) => {
        const goodOrBad = unbilled === 0 ? 'case-list__revenue-good' : 'case-list__revenue-bad';
        const title = unbilled === 0 ? 'All clear' : isPortalUser() ? 'Work completed but not yet invoiced' : 'Time to invoice?';

        return <div className='case-list__revenue'>
            <Tooltip title={title}>
                <div className={`case-list__revenue-unbilled ${goodOrBad}`}>
                    {util.formatCurrency(unbilled)}
                </div>
            </Tooltip>
        </div>;
    }, []);

    const renderMaxClientFunds = useCallback(() => {
        const msg = isPortalUser()
                    ? `You have a informed us that there is a maximum of $${matter.maxClientFunds} available for all legal services`
                    : `Client has a total of $${matter.maxClientFunds} available for all legal services`;

        return <div className='case-list__revenue'>
            <Tooltip title={msg}>
                <div className='case-list__revenue-max-client-funds'>
                    {util.formatCurrency(matter.maxClientFunds)}
                </div>
            </Tooltip>
        </div>
    }, [matter.maxClientFunds]);

    const renderDue = useCallback((due: number) => {
        const goodOrBad = due <= 0 ? 'case-list__revenue-good' : 'case-list__revenue-bad';
        const title = due === 0 ? 'All Clear' : isPortalUser() ? 'Amount due as of your last invoice' : 'Ask client for payment or use available trust';

        return <div className='case-list__revenue case-list--revenue-due'>
            <Tooltip title={title}>
                <div className={`case-list__revenue-due ${goodOrBad}`}>{util.formatCurrency(due)}</div>
            </Tooltip>
        </div>;
    }, []);

    const {
        unbilled,
        due,
        isRestricted,
        isRedRubberBanded,
        isWarning,
        warningColor
    } = FinancialHelper.getRedRubberBandInfo(matter);

    const {totalBilled, totalPaid, billingReductions} = FinancialHelper.getLifeTimeStats(matter);

    let message = null;

    const withPortal = useCallback((key: string): string => {
        if (isPortalUser()) return key + '_portal';
        else return key;
    }, []);

    if (matter.rateType !== 'c') {
        if (isWarning) message = i18n.l('caseFinancialSummary', withPortal('warning'));
        else if (isRedRubberBanded) message = i18n.l('caseFinancialSummary', withPortal('redRubberBand'));
        else if (isRestricted) message = i18n.l('caseFinancialSummary', withPortal('restricted'));
    }

    const style = {gridTemplateColumns: 'repeat(5, max-content)'};

    const financialSummaryTitle = 'Net billed is after invoice discounts and billing adjustments. Billing reductions shows those discounts and adjustments separately; a reversal can make it negative. Paid excludes money in trust that has not been applied to a bill.';

    let mainClass = 'case-financial-summary';
    if (standalone) mainClass += ' case-financial-summary--standalone';

    return <div className={mainClass}>
        <div className='case-financial-summary__dashboard' style={style}>
            <div className='case-financial-summary__metric'>
                <label>Unbilled</label>
                {renderUnbilled(unbilled)}
            </div>

            <div className='case-financial-summary__metric'>
                <label>Due</label>
                {renderDue(due)}
            </div>

            <div className='case-financial-summary__metric'>
                <label>Trust</label>
                {FinancialHelper.renderTrust(matter, {layout: 'horizontal'})}
            </div>

            {matter.maxClientFunds > 0 && <div className='case-financial-summary__metric'>
				<label>Max Funds</label>
                {renderMaxClientFunds()}
			</div>}

            {showLifeTimeStats
             ? <div className='case-financial-summary__metric'>
                 <label>Lifetime totals</label>
                 <Tooltip title={financialSummaryTitle}>
                     <dl className='case-financial-summary__lifetime'>
                         <div>
                             <dt>Net billed</dt>
                             <dd>{util.formatCurrency(totalBilled)}</dd>
                         </div>
                         <div>
                             <dt>Billing reductions</dt>
                             <dd>{util.formatCurrency(billingReductions)}</dd>
                         </div>
                         <div>
                             <dt>Paid</dt>
                             <dd>{util.formatCurrency(totalPaid)}</dd>
                         </div>
                     </dl>
                 </Tooltip>
             </div>
             : <IconButton type={Icons.CIRCLERIGHT}
                           title='Show lifetime totals'
                           color='darkgreen'
                           onClick={onMore}
                           style={{margin: 'auto 0 3px 0'}}
             />
            }

        </div>

        {!hideMessage && message && <div className={`case-financial-summary__messages ${warningColor}`}>
            {message}
		</div>
        }
    </div>;
};

export type CaseFinancialSummaryProps = {
    // Related matter
    matter: Matter,

    // Hide any warning messages?
    hideMessage?: boolean,

    // Show lifetime totals for amount billed and amount client has spent (trust + direct payments, or PT + PC - RR)
    showLifeTimeStats?: boolean,

    // Use this in a card? Leaves space to the right
    standalone?: boolean
};

export default CaseFinancialSummary;
